// OAuth authentication utilities
export interface OAuthProvider {
  name: string;
  displayName: string;
  url: string;
}

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  provider: string;
  roles: string[];
  isSubscribed: boolean;
  profilePictureUrl?: string;
}

// Get OAuth provider URLs from backend
export async function getOAuthProviders(apiUrl: string): Promise<OAuthProvider[]> {
  const response = await fetch(`${apiUrl}/api/auth/providers`, {
    credentials: 'include'
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch OAuth providers');
  }
  
  const data = await response.json();
  
  // Transform object response to array
  // Backend returns: { google: { provider: "google", authUrl: "..." }, microsoft: { ... }, salesforce: { ... } }
  return Object.entries(data).map(([key, value]: [string, any]) => ({
    name: value.provider,
    displayName: value.provider.charAt(0).toUpperCase() + value.provider.slice(1),
    url: value.authUrl
  }));
}

// Get current authenticated user (uses cookies only)
export async function getCurrentUser(apiUrl: string, retryCount = 0): Promise<CurrentUser | null> {
  console.log('🔍 Calling /api/auth/me with cookie... (attempt', retryCount + 1, ')');
  
  const response = await fetch(`${apiUrl}/api/auth/me`, {
    credentials: 'include' // Sends accessToken cookie
  });
  
  console.log('🔍 /api/auth/me response status:', response.status);
  
  if (response.status === 401) {
    // Retry once after a short delay for Mac Chrome cookie propagation
    if (retryCount === 0) {
      console.log('⚠️ 401 Unauthorized - retrying once for Mac Chrome compatibility...');
      await new Promise(resolve => setTimeout(resolve, 500));
      return getCurrentUser(apiUrl, retryCount + 1);
    }
    console.log('❌ 401 Unauthorized - no valid session after retry');
    return null;
  }
  
  if (!response.ok) {
    console.error('❌ API error:', response.status, response.statusText);
    throw new Error('Failed to fetch current user');
  }
  
  const user = await response.json();
  console.log('✅ Got user:', user);
  return user;
}

// Refresh access token using refresh token cookie
export async function refreshAccessToken(apiUrl: string): Promise<boolean> {
  const response = await fetch(`${apiUrl}/api/auth/refresh`, {
    method: 'POST',
    credentials: 'include' // Sends refreshToken cookie
  });
  
  if (!response.ok) {
    return false;
  }
  
  // Backend sets new accessToken cookie automatically
  return true;
}

// Logout and clear tokens
export async function logout(apiUrl: string): Promise<void> {
  await fetch(`${apiUrl}/api/auth/logout`, {
    method: 'POST',
    credentials: 'include' // Backend will clear cookies
  });
  
  // Clear any legacy localStorage tokens
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
}

// Start OAuth flow
export function startOAuthFlow(providerUrl: string): void {
  window.location.href = providerUrl;
}

// Check if user is authenticated (has valid access token cookie)
export async function isAuthenticated(apiUrl: string): Promise<boolean> {
  try {
    const user = await getCurrentUser(apiUrl);
    return user !== null;
  } catch {
    return false;
  }
}
